import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth.jsx";
import { Button } from "./Button";

/**
 * One-time, role-aware setup wizard. Shows automatically for any authed user
 * whose profile has no `onboarded_at` (new sign-ups AND existing users who
 * predate onboarding — they see it once on next login). Everything set here
 * can be changed later from Profile / Admin Settings.
 */

const RACE_OPTIONS = [
  ["", "Prefer not to say"],
  ["white", "White / Caucasian"],
  ["black", "Black / African"],
  ["asian", "East Asian"],
  ["south_asian", "South Asian"],
  ["hispanic", "Hispanic / Latina"],
  ["middle_eastern", "Middle Eastern"],
  ["mixed", "Mixed / Multiracial"],
  ["other", "Other"],
];

// Curated region codes; labels come from Intl.DisplayNames so we don't hand-maintain names.
const REGION_CODES = ["NG", "GH", "KE", "ZA", "US", "GB", "CA", "IE", "AU", "NZ", "AE", "IN", "FR", "DE", "NL", "BR", "MX", "JP", "SG", "MY"];
const CURRENCIES = [["NGN", "₦ Naira"], ["USD", "$ USD"], ["GBP", "£ GBP"], ["EUR", "€ Euro"], ["GHS", "₵ Cedi"], ["KES", "KSh"], ["ZAR", "R Rand"], ["CAD", "CA$"], ["AUD", "A$"], ["AED", "AED"], ["INR", "₹ INR"]];

function regionName(code) {
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code; }
  catch { return code; }
}
function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
}
function detectRegion() {
  try {
    const loc = (navigator.languages?.[0] || navigator.language || "").replace("_", "-");
    const region = loc.split("-")[1];
    if (region && /^[A-Z]{2}$/i.test(region)) return region.toUpperCase();
  } catch { /* ignore */ }
  return "";
}

export function OnboardingWizard() {
  const { session, profile, authedFetch, refreshProfile } = useAuth();
  const role = profile?.role || "customer";
  const isAdmin = role === "admin_tech";
  const isTech = role === "technician" || role === "admin_tech";

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Form state
  const [fullName, setFullName] = useState("");
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState("");
  const [skinTone, setSkinTone] = useState("");
  const [allergies, setAllergies] = useState("");
  // tech
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  // admin
  const [bizName, setBizName] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [bizPhone, setBizPhone] = useState("");
  const [currency, setCurrency] = useState("NGN");
  const [countryCode, setCountryCode] = useState("");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [depositPercent, setDepositPercent] = useState("30");
  const [silver, setSilver] = useState("");
  const [gold, setGold] = useState("");
  const [aiPrice, setAiPrice] = useState("");
  const [defaultBooking, setDefaultBooking] = useState("");
  const [tiers, setTiers] = useState([
    { label: "Simple", price: "" },
    { label: "Standard", price: "" },
    { label: "Premium", price: "" },
  ]);
  const setTier = (i, key, val) => setTiers((p) => p.map((t, idx) => idx === i ? { ...t, [key]: val } : t));

  const needsOnboarding = Boolean(session && profile && !profile.onboarded_at);

  // Prefill once when the wizard first becomes relevant.
  useEffect(() => {
    if (!needsOnboarding) return;
    setFullName(profile.full_name || "");
    setSkinTone(profile.skin_tone || "");
    setAllergies(profile.allergy_notes || "");
    const tz = profile.timezone || detectTimezone();
    setTimezone(tz);
    const detected = detectRegion();
    setCountry(profile.country || (detected ? regionName(detected) : ""));
    if (detected) setCountryCode(detected);
  }, [needsOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = useMemo(() => {
    const s = ["welcome"];
    if (isAdmin) { s.push("business", "pricing", "tiers"); }
    s.push("location", "personalize");
    if (isTech) s.push("techProfile");
    s.push("done");
    return s;
  }, [isAdmin, isTech]);

  if (!needsOnboarding) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  async function finish() {
    setSaving(true); setErr("");
    try {
      // Profile fields (everyone)
      await authedFetch("/api/account/profile", {
        method: "PATCH",
        body: JSON.stringify({
          full_name: fullName || undefined,
          country: country || undefined,
          timezone: timezone || undefined,
          skin_tone: skinTone || null,
          allergy_notes: allergies || null,
        }),
      });
      // Tech profile
      if (isTech && (bio || specialties)) {
        await authedFetch("/api/tech/me", {
          method: "PATCH",
          body: JSON.stringify({
            bio: bio || undefined,
            specialties: specialties ? specialties.split(",").map((x) => x.trim()).filter(Boolean) : undefined,
          }),
        }).catch(() => {});
      }
      // Admin studio settings
      if (isAdmin) {
        const toCents = (v) => (v === "" || v == null ? undefined : Math.round(parseFloat(v) * 100));
        const cleanTiers = tiers
          .map((t) => ({ label: (t.label || "").trim(), price_cents: toCents(t.price) }))
          .filter((t) => t.label && Number.isInteger(t.price_cents) && t.price_cents >= 0);
        const payload = {
          business_name: bizName || undefined,
          business_address: bizAddress || undefined,
          business_phone: bizPhone || undefined,
          currency_code: currency || undefined,
          country_code: countryCode || undefined,
          date_format: dateFormat || undefined,
          timezone: timezone || undefined,
          booking_deposit_enabled: true,
          booking_deposit_percent: Number(depositPercent) || 30,
          subscription_silver_cents: toCents(silver),
          subscription_gold_cents: toCents(gold),
          ai_design_price_cents: toCents(aiPrice),
          default_booking_price_cents: toCents(defaultBooking),
          design_price_tiers: cleanTiers.length ? cleanTiers : undefined,
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        await authedFetch("/api/admin/settings", { method: "PATCH", body: JSON.stringify(payload) });
      }
      await authedFetch("/api/account/onboarding/complete", { method: "POST" });
      await refreshProfile();
    } catch (e) {
      setErr(e.message || "Could not save your setup. Please try again.");
      setSaving(false);
    }
  }

  const label = { fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "11px 14px", borderRadius: 12, border: "1.5px solid var(--border-strong)", background: "var(--bg)", color: "var(--text)", fontSize: 15 };
  const field = (children) => <div style={{ marginBottom: 14 }}>{children}</div>;

  return (
    <div role="dialog" aria-modal="true" aria-label="Set up your account" style={{
      position: "fixed", inset: 0, zIndex: 2000, background: "rgba(13,8,16,0.72)",
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        width: "100%", maxWidth: 540, maxHeight: "92vh", overflowY: "auto",
        background: "var(--bg-elevated)", color: "var(--text)", borderRadius: 22,
        border: "1px solid var(--border)", boxShadow: "var(--shadow-lg)", padding: "26px 24px",
      }}>
        {/* progress */}
        <div style={{ display: "flex", gap: 5, marginBottom: 18 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= step ? "var(--accent)" : "var(--border)" }} />
          ))}
        </div>

        {current === "welcome" && (
          <div>
            <div style={{ fontSize: 30, marginBottom: 8 }}>✨</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, margin: "0 0 8px" }}>
              Welcome{fullName ? `, ${fullName.split(" ")[0]}` : ""}!
            </h2>
            <p style={{ color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }}>
              {isAdmin
                ? "Let's get your studio ready — business details, currency, pricing, and your booking deposit. It only takes a minute, and you can change anything later in Settings."
                : isTech
                  ? "Let's set up your technician profile and a few preferences so clients see the real you. You can edit everything later."
                  : "Let's personalise your experience so your AI nail designs look realistic on your hands. You can change this anytime in your profile."}
            </p>
          </div>
        )}

        {current === "business" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Studio details</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>Shown on invoices and customer-facing pages.</p>
            {field(<><span style={label}>Business name</span><input style={input} value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="Oge Eyesthetics Studio" /></>)}
            {field(<><span style={label}>Address</span><input style={input} value={bizAddress} onChange={(e) => setBizAddress(e.target.value)} placeholder="Ilorin" /></>)}
            {field(<><span style={label}>Phone</span><input style={input} value={bizPhone} onChange={(e) => setBizPhone(e.target.value)} placeholder="+234…" /></>)}
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>{field(<><span style={label}>Currency</span>
                <select style={input} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select></>)}</div>
              <div style={{ flex: 1 }}>{field(<><span style={label}>Country</span>
                <select style={input} value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                  <option value="">Select…</option>
                  {REGION_CODES.map((c) => <option key={c} value={c}>{regionName(c)}</option>)}
                </select></>)}</div>
            </div>
            {field(<><span style={label}>Date format</span>
              <select style={input} value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                {["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"].map((f) => <option key={f} value={f}>{f}</option>)}
              </select></>)}
          </div>
        )}

        {current === "tiers" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Design price tiers</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>
              Each AI-generated design is priced by mapping its difficulty to one of these tiers — customers see this price (not an AI guess). Amounts in {currency}.
            </p>
            {tiers.map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <input style={{ ...input, flex: 1 }} value={t.label} onChange={(e) => setTier(i, "label", e.target.value)} placeholder="Tier name" />
                <input style={{ ...input, width: 130 }} inputMode="decimal" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} placeholder="0" />
              </div>
            ))}
          </div>
        )}

        {current === "pricing" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Pricing & deposit</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>All amounts in {currency}. You can fine-tune tiers later in Settings → Pricing.</p>
            {field(<><span style={label}>Default booking price</span><input style={input} inputMode="decimal" value={defaultBooking} onChange={(e) => setDefaultBooking(e.target.value)} placeholder="30000" /></>)}
            {field(<><span style={label}>AI design generation (0 = free)</span><input style={input} inputMode="decimal" value={aiPrice} onChange={(e) => setAiPrice(e.target.value)} placeholder="0" /></>)}
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>{field(<><span style={label}>Silver / quarter</span><input style={input} inputMode="decimal" value={silver} onChange={(e) => setSilver(e.target.value)} placeholder="30000" /></>)}</div>
              <div style={{ flex: 1 }}>{field(<><span style={label}>Gold / quarter</span><input style={input} inputMode="decimal" value={gold} onChange={(e) => setGold(e.target.value)} placeholder="50000" /></>)}</div>
            </div>
            {field(<><span style={label}>Booking deposit %</span><input style={input} inputMode="numeric" value={depositPercent} onChange={(e) => setDepositPercent(e.target.value)} placeholder="30" /></>)}
          </div>
        )}

        {current === "location" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Where are you?</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>We detected these from your device — adjust if needed.</p>
            {field(<><span style={label}>Country</span>
              <select style={input} value={country} onChange={(e) => setCountry(e.target.value)}>
                {!REGION_CODES.map(regionName).includes(country) && country && <option value={country}>{country}</option>}
                <option value="">Select…</option>
                {REGION_CODES.map((c) => <option key={c} value={regionName(c)}>{regionName(c)}</option>)}
              </select></>)}
            {field(<><span style={label}>Timezone</span><input style={input} value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="Africa/Lagos" /></>)}
          </div>
        )}

        {current === "personalize" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Personalise your designs</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>
              Your race / ethnicity makes the AI render designs on a hand that matches your skin tone, so previews look realistic. Optional, and private.
            </p>
            {field(<><span style={label}>Race / Ethnicity</span>
              <select style={input} value={skinTone} onChange={(e) => setSkinTone(e.target.value)}>
                {RACE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></>)}
            {field(<><span style={label}>Allergies / sensitivities (optional)</span>
              <input style={input} value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="e.g. acrylic-free, latex-free" /></>)}
            {!fullName && field(<><span style={label}>Your name</span><input style={input} value={fullName} onChange={(e) => setFullName(e.target.value)} /></>)}
          </div>
        )}

        {current === "techProfile" && (
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 4px" }}>Your tech profile</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>Clients see this when choosing a technician.</p>
            {field(<><span style={label}>Short bio</span>
              <textarea style={{ ...input, minHeight: 84, resize: "vertical" }} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Gel specialist with 6 years of bridal experience…" /></>)}
            {field(<><span style={label}>Specialties (comma-separated)</span>
              <input style={input} value={specialties} onChange={(e) => setSpecialties(e.target.value)} placeholder="gel, ombre, nail art, bridal" /></>)}
          </div>
        )}

        {current === "done" && (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 23, margin: "0 0 8px" }}>You're all set!</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }}>
              {isAdmin ? "Your studio is configured. " : ""}You can change any of this later
              {isAdmin ? " from Admin → Settings." : " from your Profile."}
            </p>
          </div>
        )}

        {err && <div style={{ color: "var(--danger, #b42318)", fontSize: 13, marginTop: 12 }}>{err}</div>}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 22 }}>
          <Button variant="ghost" label="Back" onClick={back} disabled={step === 0 || saving} />
          {isLast ? (
            <Button variant="gold" label={saving ? "Saving…" : "Finish setup"} onClick={finish} loading={saving} disabled={saving} />
          ) : (
            <Button variant="primary" label="Continue" onClick={next} />
          )}
        </div>
      </div>
    </div>
  );
}
